import React, { FC } from 'react';
import { QueryClient, useQuery } from '@tanstack/react-query';
import type { Request } from 'express';

import { SeoSlugEnum, SERVICE_TYPE_FOR_SEO_SLUG } from '@rover/constants/src/search/seo.constants';
import { RoverLoading } from '@rover/react-lib/src/components';
import makeSearchQuery from '@rover/react-lib/src/pages/landing/utilities/makeSearchQuery';
import getQueryError from '@rover/react-lib/src/utils/getQueryError';
import {
  apiFrontendConfigurationRetrieve,
  getApiFrontendConfigurationRetrieveQueryKey,
} from '@rover/rsdk/src/apiClient/latest/api-frontend-configuration-retrieve';
import { ApiFrontendConfigurationRetrieve200 } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendConfigurationRetrieve200';
import { ApiSearchOptimizedRetrievePetType } from '@rover/rsdk/src/apiClient/latest/model/apiSearchOptimizedRetrievePetType';
import { HttpError } from '@rover/rsdk/src/errors';
import {
  FLAGS,
  isFlagActiveForConfig,
} from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import useFlag from '@rover/shared/js/hooks/useFlag';
import getPath from '@rover/shared/js/utils/getPath';
import { Http404Error, Http500Error } from '@rover/utilities/HttpError';
import { pathToLocality } from '@rover/utilities/locality';

import InternalLinkingPage from './InternalLinkingPage';
import { getInternalLinkingPrefetchQueryOptions, useInternalLinking } from './useInternalLinking';

const InternalLinkingPageContainer: FC & { reactQueryPrefetch: any } = () => {
  const path = `${getPath()}/`;
  const locality = pathToLocality(path);
  const { data, isLoading, isError, error } = useInternalLinking(locality);
  const isFlagActive = useFlag(FLAGS.ROLLOUT_GEO_BROWSE_INTERNAL_LINKING_PAGE);

  const location = data?.results?.metadata?.location;
  const locationType = data?.results?.metadata?.locationType;
  const isRegion = locationType === 'REGION';

  const searchQueryOptions = {
    locationParams: location
      ? { centerlat: Number(location.lat), centerlng: Number(location.lng) }
      : {},
    enabled: isFlagActive && !isRegion,
  };

  const { data: boardingResults } = useQuery(
    makeSearchQuery(
      {
        serviceType: SERVICE_TYPE_FOR_SEO_SLUG[SeoSlugEnum.DOG_BOARDING],
        petType: ApiSearchOptimizedRetrievePetType.dog,
      },
      searchQueryOptions
    )
  );
  const { data: largeDogBoardingResults } = useQuery(
    makeSearchQuery(
      {
        serviceType: SERVICE_TYPE_FOR_SEO_SLUG[SeoSlugEnum.DOG_BOARDING],
        petType: ApiSearchOptimizedRetrievePetType.dog,
        largeDogs: true,
      },
      searchQueryOptions
    )
  );
  const { data: catDropInResults } = useQuery(
    makeSearchQuery(
      {
        serviceType: SERVICE_TYPE_FOR_SEO_SLUG[SeoSlugEnum.CAT_SITTING],
        petType: ApiSearchOptimizedRetrievePetType.cat,
      },
      searchQueryOptions
    )
  );
  const { data: dayCareResults } = useQuery(
    makeSearchQuery(
      {
        serviceType: SERVICE_TYPE_FOR_SEO_SLUG[SeoSlugEnum.DOGGY_DAY_CARE],
        petType: ApiSearchOptimizedRetrievePetType.dog,
      },
      searchQueryOptions
    )
  );
  const { data: walkingResults } = useQuery(
    makeSearchQuery(
      {
        serviceType: SERVICE_TYPE_FOR_SEO_SLUG[SeoSlugEnum.DOG_WALKING],
        petType: ApiSearchOptimizedRetrievePetType.dog,
      },
      searchQueryOptions
    )
  );

  if (isError || !data) {
    if (error instanceof HttpError && error?.statusCode === 404) {
      throw new Http404Error();
    }
    throw new Http500Error();
  }

  if (isLoading) {
    return (
      <RoverLoading style={{ height: '100vh' }} data-testid="rover-loading" isLoading={isLoading} />
    );
  }

  return (
    <InternalLinkingPage
      {...data.results}
      boardingResults={boardingResults}
      largeDogBoardingResults={largeDogBoardingResults}
      catDropInResults={catDropInResults}
      dayCareResults={dayCareResults}
      walkingResults={walkingResults}
    />
  );
};

InternalLinkingPageContainer.reactQueryPrefetch = async (
  queryClient: QueryClient,
  req: Request
) => {
  const path = `${req.path}/`;
  const locality = pathToLocality(path);

  const internalLinkingData = await queryClient
    .fetchQuery(getInternalLinkingPrefetchQueryOptions(locality))
    .catch((error) => {
      throw getQueryError(error) ?? error;
    });

  // Check if the flag is active using the frontend configuration from the query cache
  const frontendConfig =
    queryClient.getQueryData<ApiFrontendConfigurationRetrieve200>(
      getApiFrontendConfigurationRetrieveQueryKey()
    ) ??
    (await queryClient.fetchQuery({
      queryKey: getApiFrontendConfigurationRetrieveQueryKey(),
      queryFn: () => apiFrontendConfigurationRetrieve(),
    }));
  const isFlagActive = isFlagActiveForConfig(
    frontendConfig?.flags ?? {},
    FLAGS.ROLLOUT_GEO_BROWSE_INTERNAL_LINKING_PAGE
  );

  const location = internalLinkingData?.results?.metadata?.location;
  const locationType = internalLinkingData?.results?.metadata?.locationType;
  const isRegion = locationType === 'REGION';

  if (!isFlagActive || isRegion) {
    return;
  }

  const locationParams = location
    ? { centerlat: Number(location.lat), centerlng: Number(location.lng) }
    : {};

  const searchQueries = [
    {
      serviceType: SERVICE_TYPE_FOR_SEO_SLUG[SeoSlugEnum.DOG_BOARDING],
      petType: ApiSearchOptimizedRetrievePetType.dog,
    },
    {
      serviceType: SERVICE_TYPE_FOR_SEO_SLUG[SeoSlugEnum.DOG_BOARDING],
      petType: ApiSearchOptimizedRetrievePetType.dog,
      largeDogs: true,
    },
    {
      serviceType: SERVICE_TYPE_FOR_SEO_SLUG[SeoSlugEnum.CAT_SITTING],
      petType: ApiSearchOptimizedRetrievePetType.cat,
    },
    {
      serviceType: SERVICE_TYPE_FOR_SEO_SLUG[SeoSlugEnum.DOGGY_DAY_CARE],
      petType: ApiSearchOptimizedRetrievePetType.dog,
    },
    {
      serviceType: SERVICE_TYPE_FOR_SEO_SLUG[SeoSlugEnum.DOG_WALKING],
      petType: ApiSearchOptimizedRetrievePetType.dog,
    },
  ];

  await Promise.all(
    searchQueries.map((params) => {
      const query = makeSearchQuery(params, { locationParams });
      return queryClient.prefetchQuery({ queryKey: query.queryKey!, queryFn: query.queryFn! });
    })
  );
};

export default InternalLinkingPageContainer;
